import { useId, useMemo } from "react"
import { type TIntegration, type TOAuthClientType } from "./store"

export function PlatformIcon({
	platform,
	clientType,
	size = "24px",
}: {
	platform: TIntegration["platform"]
	/** For an OAuth app, the client it was created for; picks the vendor's mark. */
	clientType?: TOAuthClientType
	size?: string
}) {
	if (platform === "oauth") {
		return <OAuthClientIcon clientType={clientType} size={size} />
	}
	switch (platform) {
		case "google_ads":
			return <GoogleAds size={size} />
		case "meta":
			return <Meta size={size} />
		case "pg_payu":
			return <PayU size={size} />
		case "pg_simulator":
			return <PGSimulator size={size} />
		case "api":
			return <API size={size} />
		case "email_mailbox":
			return <Mailbox size={size} />
		case "whatsapp_cloud_api":
			return <WhatsAppCloudApi size={size} />
		case "whatsapp_simulator":
			return <WhatsAppSimulator size={size} />
		case "tc_knowlarity":
			return <Knowlarity size={size} />
		case "tc_callerdesk":
			return <CallerDesk size={size} />
		case "tc_tatatele":
			return <TataTele size={size} />
		default:
			break
	}
	return null
}

/**
 * The mark of the client an OAuth app was created for: Claude's or
 * OpenAI's, or a generic key for any other OAuth client.
 */
export function OAuthClientIcon({
	clientType,
	size = "24px",
}: {
	clientType?: TOAuthClientType
	size?: string
}) {
	switch (clientType) {
		case "claude":
			return <Claude size={size} />
		case "chatgpt":
			return <OpenAI size={size} />
		default:
			return <OAuthApp size={size} />
	}
}

function Claude({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			role="img"
			aria-label="Claude"
			fill="#D97757"
		>
			<path d="M4.709 15.955l4.72-2.647.08-.23-.08-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.784l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.449.255h.389l.055-.157-.134-.098-.103-.097-2.358-1.596-2.552-1.688-1.336-.972-.724-.491-.364-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.146-.103.019-.073-.164-.274-1.355-2.446-1.446-2.49-.644-1.032-.17-.619a2.97 2.97 0 01-.104-.729L6.283.134 6.696 0l.996.134.42.364.62 1.414 1.002 2.229 1.555 3.03.456.898.243.832.091.255h.158V9.01l.128-1.706.237-2.095.23-2.695.08-.76.376-.91.747-.492.584.28.48.685-.067.444-.286 1.851-.559 2.903-.364 1.942h.212l.243-.242.985-1.306 1.652-2.064.73-.82.85-.904.547-.431h1.033l.76 1.129-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.091.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.049.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.91-1.312-.329h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.657-.851-.747-1.926-1.62h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.608.213-.668-.122-1.374-1.925-1.415-2.167-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.924.315-1.53.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.067-.662.401-.589 2.388-3.036 1.44-1.882.93-1.086-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.231-.243 1.908-1.312-.006.006z" />
		</svg>
	)
}

function OpenAI({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			role="img"
			aria-label="OpenAI"
			fill="currentColor"
		>
			<path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
		</svg>
	)
}

function OAuthApp({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
			<circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
		</svg>
	)
}

function GoogleAds({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			version="1.1"
			width={size}
			height={size}
			viewBox="0 0 192 192"
		>
			<rect
				x="8"
				y="62.52"
				transform="matrix(0.5 -0.866 0.866 0.5 -46.2127 103.666)"
				fill="#FBBC04"
				width="117.33"
				height="58.67"
			/>
			<path
				fill="#4285F4"
				d="M180.07,127.99L121.4,26.38c-8.1-14.03-26.04-18.84-40.07-10.74c-14.03,8.1-18.84,26.04-10.74,40.07   l58.67,101.61c8.1,14.03,26.04,18.83,40.07,10.74C183.36,159.96,188.16,142.02,180.07,127.99z"
			/>
			<circle fill="#34A853" cx="37.34" cy="142.66" r="29.33" />
		</svg>
	)
}

function Meta({ size = "24px" }: { size?: string }) {
	const id = useId()
	const width = size
	const height = useMemo(() => {
		const original_width = 72
		const original_height = 48
		const aspectRatio = original_width / original_height
		return (
			String(Math.ceil(Number(size.replace("px", "")) / aspectRatio)) + "px"
		)
	}, [size])
	return (
		<svg
			aria-label="Meta symbol"
			height={height}
			width={width}
			role="img"
			viewBox="0 0 150 100"
		>
			<defs>
				<linearGradient
					gradientUnits="userSpaceOnUse"
					id={`meta_logo_${id}_0`}
					x1="124.599"
					x2="159.703"
					y1="96.689"
					y2="57.25"
				>
					<stop offset="0.21" stopColor="#0278F1"></stop>
					<stop offset="0.533" stopColor="#0180FA"></stop>
				</linearGradient>
				<linearGradient
					gradientUnits="userSpaceOnUse"
					id={`meta_logo_${id}_1`}
					x1="43.859"
					x2="0"
					y1="4.984"
					y2="63.795"
				>
					<stop offset="0.427" stopColor="#0165E0"></stop>
					<stop offset="0.917" stopColor="#0180FA"></stop>
				</linearGradient>
				<linearGradient
					gradientUnits="userSpaceOnUse"
					id={`meta_logo_${id}_2`}
					x1="28.409"
					x2="134.567"
					y1="28.907"
					y2="71.769"
				>
					<stop stopColor="#0064E0"></stop>
					<stop offset="0.656" stopColor="#0066E2"></stop>
					<stop offset="1" stopColor="#0278F1"></stop>
				</linearGradient>
			</defs>
			<path
				d="M107.654 0c-12.3 0-21.915 9.264-30.618 21.032C65.076 5.802 55.073 0 43.103 0 18.698 0 0 31.76 0 65.376 0 86.41 10.177 99.679 27.223 99.679c12.268 0 21.092-5.784 36.778-33.203 0 0 6.539-11.547 11.037-19.501a514.1 514.1 0 0 1 4.98 8.227l7.356 12.374c14.329 23.977 22.312 32.103 36.778 32.103C140.758 99.68 150 86.23 150 64.757 150 29.56 130.88 0 107.654 0ZM52.039 59.051C39.322 78.987 34.922 83.455 27.841 83.455c-7.287 0-11.617-6.397-11.617-17.804 0-24.404 12.167-49.359 26.672-49.359 7.855 0 14.42 4.537 24.474 18.93-9.547 14.645-15.33 23.83-15.33 23.83Zm47.999-2.51-8.795-14.667c-2.38-3.87-4.66-7.428-6.862-10.689 7.927-12.234 14.465-18.33 22.241-18.33 16.155 0 29.079 23.786 29.079 53.002 0 11.137-3.647 17.599-11.205 17.599-7.244 0-10.704-4.785-24.458-26.914Z"
				fill="#0180FA"
			></path>
			<path
				d="M145.119 34.888h-14.878c3.44 8.718 5.46 19.42 5.46 30.969 0 11.137-3.647 17.599-11.205 17.599-1.403 0-2.663-.18-3.884-.63V99.5a33.48 33.48 0 0 0 3.54.18C140.758 99.68 150 86.23 150 64.756c0-10.702-1.768-20.882-4.881-29.87Z"
				fill={`url(#meta_logo_${id}_0)`}
			></path>
			<path
				d="M43.103 0c.253 0 .505.003.756.008v16.308c-.319-.016-.64-.024-.962-.024-14.138 0-26.055 23.706-26.65 47.503H.014C.588 30.77 19.08 0 43.103 0Z"
				fill={`url(#meta_logo_${id}_1)`}
			></path>
			<path
				d="M43.103 0c11.115 0 20.534 5.004 31.4 17.913 3.055 3.817 6.74 8.777 10.682 14.511l.017.025.024-.003c1.939 2.912 3.94 6.05 6.017 9.428l8.795 14.668c13.754 22.13 17.214 26.913 24.458 26.913h.103v16.22c-.148.003-.297.004-.447.004-14.466 0-22.449-8.126-36.778-32.103l-7.356-12.374a587.065 587.065 0 0 0-2.287-3.822l.019-.045c-5.483-8.971-8.971-14.454-11.962-18.44l-.05.037c-9.15-12.59-15.42-16.64-22.842-16.64h-.034V.001l.24-.001Z"
				fill={`url(#meta_logo_${id}_2)`}
			></path>
		</svg>
	)
}

function API({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			width={size}
			height={size}
		>
			<path
				fillRule="evenodd"
				d="M3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6Zm14.25 6a.75.75 0 0 1-.22.53l-2.25 2.25a.75.75 0 1 1-1.06-1.06L15.44 12l-1.72-1.72a.75.75 0 1 1 1.06-1.06l2.25 2.25c.141.14.22.331.22.53Zm-10.28-.53a.75.75 0 0 0 0 1.06l2.25 2.25a.75.75 0 1 0 1.06-1.06L8.56 12l1.72-1.72a.75.75 0 1 0-1.06-1.06l-2.25 2.25Z"
				clipRule="evenodd"
			/>
		</svg>
	)
}

function PayU({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			id="Layer_1"
			data-name="Layer 1"
			viewBox="0 0 514.56 280.44"
			width={size}
			height={size}
		>
			<path
				className="cls-1"
				fill="#414042"
				strokeWidth="0"
				d="M0,97.77h25.72v12.86c2.93-4.46,7.37-8.1,13.3-10.92,5.93-2.82,12.3-4.23,19.11-4.23,8.1,0,15.56,2.06,22.37,6.17,6.81,4.11,12.27,9.98,16.38,17.62,4.11,7.64,6.16,16.56,6.16,26.78s-2.06,19.29-6.16,26.86c-4.11,7.58-9.6,13.36-16.47,17.35-6.87,3.99-14.3,5.99-22.28,5.99-6.93,0-13.36-1.38-19.29-4.14-5.93-2.76-10.31-6.37-13.12-10.83v99.18H0V97.77ZM69.67,165.94c4.76-5.17,7.13-11.8,7.13-19.9s-2.38-14.94-7.13-20.17c-4.76-5.22-10.89-7.84-18.41-7.84s-13.68,2.61-18.5,7.84c-4.82,5.23-7.22,11.95-7.22,20.17s2.41,14.74,7.22,19.9c4.81,5.17,10.98,7.75,18.5,7.75s13.65-2.58,18.41-7.75ZM134.67,190.25c-6.87-3.99-12.36-9.78-16.47-17.35-4.11-7.58-6.17-16.53-6.17-26.86s2.05-19.14,6.17-26.78c4.11-7.63,9.57-13.5,16.38-17.62,6.81-4.11,14.27-6.17,22.37-6.17,6.81,0,13.18,1.41,19.11,4.23,5.93,2.82,10.36,6.46,13.3,10.92v-12.33h25.72v95.13h-25.72v-12.15c-2.82,4.46-7.2,8.07-13.12,10.83-5.93,2.76-12.36,4.14-19.29,4.14-7.99,0-15.41-2-22.28-5.99ZM182.41,165.94c4.76-5.17,7.14-11.8,7.14-19.9s-2.38-14.94-7.14-20.17c-4.76-5.22-10.89-7.84-18.41-7.84s-13.68,2.61-18.5,7.84c-4.82,5.23-7.22,11.95-7.22,20.17s2.41,14.74,7.22,19.9c4.81,5.17,10.98,7.75,18.5,7.75s13.65-2.58,18.41-7.75ZM272.69,196.24c-14.56,0-25.31-3.7-32.24-11.1-6.93-7.4-10.39-17.73-10.39-31v-56.37h26.07v53.73c0,7.4,1.94,12.98,5.81,16.73,3.88,3.76,9.22,5.64,16.03,5.64,6.22,0,11.6-2.05,16.12-6.17,4.52-4.11,6.78-9.69,6.78-16.73v-53.2h25.72v182.68h-25.72v-96.71c-6.11,8.34-15.5,12.51-28.19,12.51ZM348.44,252.44l142.86-43.16V75.57l-164.71-41.93v40.87h-25.72V0l213.68,56.19v171.75l-166.12,51.62v-27.13Z"
			/>
			<path
				className="cls-2"
				fill="#00ad7d"
				d="M362.18,97.77h26.07v53.73c0,15.03,7.1,22.55,21.32,22.55s21.32-7.51,21.32-22.55v-53.73h26.07v56.37c0,13.15-4.05,23.46-12.15,30.91-8.1,7.46-19.85,11.19-35.23,11.19s-27.13-3.76-35.23-11.27c-8.1-7.52-12.16-17.79-12.16-30.83v-56.37Z"
			/>
		</svg>
	)
}

function PGSimulator({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" />
			<path d="M6.453 15h11.094" />
			<path d="M8.5 2h7" />
		</svg>
	)
}

function WhatsAppCloudApi({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 360 362"
			fill="none"
			width={size}
			height={size}
		>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M307.546 52.5655C273.709 18.685 228.706 0.0171895 180.756 0C81.951 0 1.53846 80.404 1.50408 179.235C1.48689 210.829 9.74646 241.667 25.4319 268.844L0 361.736L95.0236 336.811C121.203 351.096 150.683 358.616 180.679 358.625H180.756C279.544 358.625 359.966 278.212 360 179.381C360.017 131.483 341.392 86.4547 307.546 52.5741V52.5655ZM180.756 328.354H180.696C153.966 328.346 127.744 321.16 104.865 307.589L99.4242 304.358L43.034 319.149L58.0834 264.168L54.5423 258.53C39.6304 234.809 31.749 207.391 31.7662 179.244C31.8006 97.1036 98.6334 30.2707 180.817 30.2707C220.61 30.2879 258.015 45.8015 286.145 73.9665C314.276 102.123 329.755 139.562 329.738 179.364C329.703 261.513 262.871 328.346 180.756 328.346V328.354ZM262.475 216.777C257.997 214.534 235.978 203.704 231.869 202.209C227.761 200.713 224.779 199.966 221.796 204.452C218.814 208.939 210.228 219.029 207.615 222.011C205.002 225.002 202.389 225.372 197.911 223.128C193.434 220.885 179.003 216.158 161.891 200.902C148.578 189.024 139.587 174.362 136.975 169.875C134.362 165.389 136.7 162.965 138.934 160.739C140.945 158.728 143.412 155.505 145.655 152.892C147.899 150.279 148.638 148.406 150.133 145.423C151.629 142.432 150.881 139.82 149.764 137.576C148.646 135.333 139.691 113.287 135.952 104.323C132.316 95.5909 128.621 96.777 125.879 96.6309C123.266 96.5019 120.284 96.4762 117.293 96.4762C114.302 96.4762 109.454 97.5935 105.346 102.08C101.238 106.566 89.6691 117.404 89.6691 139.441C89.6691 161.478 105.716 182.785 107.959 185.776C110.202 188.767 139.544 234.001 184.469 253.408C195.153 258.023 203.498 260.782 210.004 262.845C220.731 266.257 230.494 265.776 238.212 264.624C246.816 263.335 264.71 253.786 268.44 243.326C272.17 232.866 272.17 223.893 271.053 222.028C269.936 220.163 266.945 219.037 262.467 216.794L262.475 216.777Z"
				fill="#25D366"
			/>
		</svg>
	)
}

function WhatsAppSimulator({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			width={size}
			height={size}
		>
			<path
				fill-rule="evenodd"
				d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z"
				clip-rule="evenodd"
			/>
		</svg>
	)
}

function Mailbox({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<rect x="2" y="4" width="20" height="16" rx="2" />
			<path d="m22 7-8.97 5.7a2 2 0 0 1-2.06 0L2 7" />
		</svg>
	)
}
function CallerDesk({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 96 96"
		>
			<path
				fill="#d40000"
				d="M42 24.9c-12.8 3.9-19.9 15.9-17.1 28.5 1.9 8.6 9.3 15.9 18.4 18.1 2.6.7 2.7.6 2.7-3.7 0-4-.4-4.7-3.7-6.9-5.1-3.4-7.5-8.2-7-14.1.5-5.6 2.7-9.3 7.3-12 3.1-1.8 3.4-2.4 3.4-6.4 0-2.4-.3-4.4-.7-4.3-.5 0-1.9.4-3.3.8"
			/>
			<g stroke-width="0">
				<path d="M48 26.4c0 .8 1.9 1.8 4.5 2.5C60.8 31 68 39.9 68 48s-7.2 17-15.5 19.1C47.3 68.4 46.3 71 51 71c4 0 8.7-2 12.8-5.5 10.4-8.7 10.4-26.3 0-35-5.9-5-15.8-7.5-15.8-4.1" />
				<path d="M48.7 32.7c-1.6 1.5-.5 3.2 2.7 4.3 10.8 3.5 11.2 17.6.6 21.7-3.9 1.6-5.1 3.5-3 4.8.6.4 3.2-.4 5.8-1.7C60 59.2 64 53 64 47.5c0-4.1-3.8-10.5-7.5-12.6-3.9-2.2-7-3.1-7.8-2.2" />
				<path d="M48 48c0 4.4.2 8 .5 8 .2 0 1.5-.5 2.9-1.1 3.5-1.6 5.2-6.1 3.7-9.8-1-2.3-4.6-5.1-6.6-5.1-.3 0-.5 3.6-.5 8" />
			</g>
		</svg>
	)
}

function Knowlarity({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 360 360"
			fill="none"
		>
			<path
				d="M187.094 21.254C195.943 29.359 202.287 38.638 203 51l.25 3.688c-.229 11.84-4.994 21.967-12.937 30.562-9.125 8.602-19.016 11.472-31.188 12.188-15.757 1-26.92 6.657-38.125 17.562q-1.747 1.604-3.496 3.203c-11.337 10.577-19.167 22.154-19.84 38.188-.302 15.253-.302 15.253 4.336 29.609l1.355 2.688c2.715 4.89 5.785 8.8 9.872 12.601 1.862 1.797 3.45 3.707 5.085 5.711 7.625 8.763 17.456 13.53 28.688 16a82 82 0 0 0 4.309.176c15.375.387 27.818 2.123 39.691 12.824 2.332 2.507 4.227 5.074 6 8l1.176 1.93c5.907 10.696 6.007 22.529 2.863 34.078-3.88 11.17-11.628 19.58-22.039 24.992-6.129 2.583-11.75 3.524-18.375 3.438l-2.515-.026c-13.066-.358-21.922-5.834-30.86-15.1-7.806-9.595-8.814-19.606-10.094-31.507-2.27-19.905-15.16-34.867-30.226-47.477-9.312-7.057-18.332-9.784-29.868-10.828-15.802-1.436-26.916-5.967-37.304-18.148-7.01-9.38-8.45-20.42-7.348-31.829 1.937-11.57 7.82-20.087 16.89-27.37 8.006-5.628 16.117-8.114 25.88-8.392 20.792-.66 34.08-8.896 48.191-23.69 10.465-11.423 13.6-23.331 14.227-38.439.628-13.399 5.193-24.496 15.074-33.812 15.784-12.34 38.572-13.59 54.422-.566"
				fill="#423585"
			/>
			<path
				d="m294 128 1.895 1.52c6.46 5.785 11.518 14.763 12.282 23.504.424 14.783-.72 25.686-11.177 36.976-8.734 9.001-18.54 12.318-30.875 13.313-11.33-.367-22.073-4.867-30.43-12.465-8.818-9.61-12.406-20.615-12.039-33.516 1.298-12.584 6.57-22.535 16.156-30.707C256.209 114.272 278.11 115.106 294 128m-2.504-108.012C299.534 26.446 305.79 34.778 308 45c1.156 13.216.475 25.47-8.254 36.094-8.279 9.038-17.728 15.083-30.219 16.11-13.038.235-23.853-3.212-33.593-12.087-8.94-9.538-12.506-20.828-12.239-33.683 1.035-11.661 5.853-21.103 14.18-29.274 15.54-12.723 37.227-13.555 53.621-2.172"
				fill="#742c89"
			/>
			<path
				d="M79.438 231.313c9.043 6.907 15.23 16.01 17.277 27.355 1.475 12.067-.793 23.423-8.102 33.344L87 294l-1.52 1.895c-5.785 6.46-14.763 11.518-23.504 12.282C47.18 308.601 36.313 307.449 25 297c-8.46-8.17-12.833-18.625-13.066-30.32C12.4 253.81 16.767 243.09 26 234c15.613-12.557 36.586-13.888 53.438-2.687"
				fill="#323983"
			/>
			<path
				d="M184.652 125.285c9.232 6.744 15.012 15.422 17.786 26.59 1.858 12.08-.194 23.95-7.454 33.883-8.298 9.72-18.118 16.093-30.984 17.242-11.125.754-21.012-2.466-30-9-2.812-2.5-2.812-2.5-5-5l-2.562-2.812c-7.302-9-9.444-19.808-8.438-31.188 2.166-12.253 8.08-23.007 18.313-30.25 15.024-9.501 33.516-9.073 48.34.535"
				fill="#4c3385"
			/>
			<path
				d="M82.375 22.25c8.3 8.033 14.028 17.587 14.793 29.328.095 13-3.193 23.915-12.219 33.606-9.435 8.792-20.258 12.187-33.015 12.03C41.716 96.468 31.382 92.188 24 85c-8.452-10.454-12.354-20.456-12-34 1.38-11.668 6.121-21.119 14.672-29.18 16.587-12.97 39.522-13.385 55.703.43"
				fill="#323983"
			/>
			<path
				d="M290.375 230.813A128 128 0 0 1 294 234l1.828 1.52c6.52 5.824 11.52 14.688 12.365 23.488.55 14.438-1.089 25.89-11.193 36.992-5.67 6.012-14.539 11.401-22.91 12.177-14.137.402-25.062-.46-36.09-10.177l-1.871-1.59c-7.645-6.987-11.568-16.367-12.383-26.598-.451-13.282 3.259-24.135 12.219-33.968 14.122-13.688 38.031-16.803 54.41-5.031"
				fill="#742c89"
			/>
		</svg>
	)
}

function TataTele({ size = "24px" }: { size?: string }) {
	const id = useId()
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 287 56"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M0.359131 55.6302C1.17366 55.7428 2.47049 55.8553 4.16922 55.8553C7.26659 55.8553 9.4101 55.2872 10.7391 54.076C11.6983 53.1435 12.3467 51.9055 12.3467 50.2655C12.3467 47.4465 10.2354 45.9512 8.42945 45.501V45.4474C10.4283 44.7132 11.6447 43.1107 11.6447 41.2725C11.6447 39.7772 11.0499 38.6518 10.0639 37.9175C8.87959 36.9582 7.29875 36.5294 4.84443 36.5294C3.12426 36.5294 1.43089 36.7009 0.36449 36.926V55.6302H0.359131ZM2.80809 38.5928C3.20464 38.5071 3.85305 38.4267 4.9784 38.4267C7.46487 38.4267 9.15288 39.3002 9.15288 41.5244C9.15288 43.3573 7.63099 44.7132 5.03734 44.7132H2.80809V38.5928ZM2.80809 46.5783H4.83907C7.51846 46.5783 9.74771 47.6501 9.74771 50.2441C9.74771 53.0095 7.40592 53.942 4.86586 53.942C3.99238 53.942 3.29038 53.9152 2.80809 53.8295V46.5783ZM27.2655 42.0389H24.7791V50.4156C24.7791 50.8657 24.6933 51.3213 24.554 51.6857C24.1039 52.7844 22.9464 53.942 21.2852 53.942C19.0291 53.942 18.2414 52.1949 18.2414 49.5956V42.0389H15.7603V50.019C15.7603 54.8156 18.3218 56 20.4653 56C22.8928 56 24.3289 54.5583 24.9827 53.4597H25.0363L25.1756 55.6838H27.3781C27.2923 54.6119 27.2655 53.3739 27.2655 51.9644V42.0389ZM30.7059 55.0407C31.6651 55.6034 33.0423 55.9679 34.5428 55.9679C37.7848 55.9679 39.6443 54.2797 39.6443 51.8787C39.6443 49.8475 38.4332 48.6684 36.0647 47.7627C34.2855 47.082 33.4657 46.5783 33.4657 45.4474C33.4657 44.4345 34.2855 43.5877 35.7485 43.5877C37.0185 43.5877 38.0045 44.0379 38.5404 44.3809L39.162 42.5748C38.4011 42.1193 37.19 41.728 35.8074 41.728C32.8762 41.728 31.0971 43.5288 31.0971 45.7315C31.0971 47.3661 32.2546 48.722 34.7035 49.5956C36.5362 50.2762 37.2436 50.9247 37.2436 52.1091C37.2436 53.2346 36.3969 54.1403 34.591 54.1403C33.3531 54.1403 32.0509 53.6312 31.3221 53.1488L30.7059 55.0407ZM45.5979 55.6838V42.0389H43.1115V55.6892H45.5979V55.6838ZM44.3547 36.6527C43.4544 36.6527 42.806 37.328 42.806 38.2016C42.806 39.0483 43.4276 39.7236 44.3011 39.7236C45.2871 39.7236 45.9087 39.0483 45.8766 38.2016C45.8766 37.3226 45.2871 36.6527 44.3547 36.6527ZM49.9975 55.6838H52.484V47.4786C52.484 47.0552 52.5375 46.6318 52.6501 46.321C53.0734 44.9383 54.3434 43.7807 55.9779 43.7807C58.3143 43.7807 59.1342 45.6136 59.1342 47.8109V55.6785H61.6153V47.5376C61.6153 42.8535 58.6841 41.728 56.7978 41.728C54.5364 41.728 52.9609 42.9982 52.2803 44.2898H52.2267L52.0874 42.0335H49.8849C49.9707 43.159 49.9975 44.3166 49.9975 45.7315V55.6838ZM76.9039 49.3169C76.9307 49.065 76.9896 48.6684 76.9896 48.1593C76.9896 45.6511 75.8053 41.728 71.3522 41.728C67.376 41.728 64.9485 44.9704 64.9485 49.0864C64.9485 53.2078 67.4617 55.9679 71.663 55.9679C73.8333 55.9679 75.3338 55.5177 76.2073 55.1211L75.7839 53.3418C74.8515 53.7384 73.7797 54.0438 72.006 54.0438C69.5195 54.0438 67.3813 52.6611 67.3224 49.3062H76.9039V49.3169ZM67.3438 47.5376C67.5368 45.8172 68.6353 43.502 71.1486 43.502C73.9405 43.502 74.6211 45.9566 74.5889 47.5376H67.3438ZM79.385 55.0407C80.3442 55.6034 81.7214 55.9679 83.2165 55.9679C86.464 55.9679 88.3234 54.2797 88.3234 51.8787C88.3234 49.8475 87.1124 48.6684 84.7384 47.7627C82.9647 47.082 82.1448 46.5783 82.1448 45.4474C82.1448 44.4345 82.9647 43.5877 84.4276 43.5877C85.6976 43.5877 86.6837 44.0379 87.2195 44.3809L87.8412 42.5748C87.0802 42.1193 85.8691 41.728 84.4812 41.728C81.5499 41.728 79.7762 43.5288 79.7762 45.7315C79.7762 47.3661 80.9337 48.722 83.3827 49.5956C85.2154 50.2762 85.9227 50.9247 85.9227 52.1091C85.9227 53.2346 85.076 54.1403 83.2701 54.1403C82.0322 54.1403 80.7301 53.6312 80.0013 53.1488L79.385 55.0407ZM90.8367 55.0407C91.7959 55.6034 93.1731 55.9679 94.6682 55.9679C97.9157 55.9679 99.7752 54.2797 99.7752 51.8787C99.7752 49.8475 98.5641 48.6684 96.1901 47.7627C94.411 47.082 93.5965 46.5783 93.5965 45.4474C93.5965 44.4345 94.411 43.5877 95.8793 43.5877C97.1494 43.5877 98.1354 44.0379 98.6712 44.3809L99.2929 42.5748C98.5319 42.1193 97.3155 41.728 95.9329 41.728C93.0017 41.728 91.2279 43.5288 91.2279 45.7315C91.2279 47.3661 92.38 48.722 94.8344 49.5956C96.6671 50.2762 97.3744 50.9247 97.3744 52.1091C97.3744 53.2346 96.5277 54.1403 94.7218 54.1403C93.4839 54.1403 92.1818 53.6312 91.453 53.1488L90.8367 55.0407ZM108.349 54.7566C109.362 55.4319 111.366 55.9679 113.199 55.9679C117.684 55.9679 119.854 53.4275 119.854 50.4959C119.854 47.7037 118.22 46.1549 115.005 44.9115C112.384 43.8986 111.227 43.0196 111.227 41.2457C111.227 39.9541 112.213 38.3999 114.812 38.3999C116.532 38.3999 117.802 38.9626 118.418 39.3056L119.099 37.3012C118.252 36.8242 116.843 36.374 114.898 36.374C111.2 36.374 108.751 38.5714 108.751 41.5351C108.751 44.2148 110.664 45.8226 113.767 46.9266C116.334 47.9127 117.352 48.931 117.352 50.705C117.352 52.6236 115.884 53.9474 113.376 53.9474C111.682 53.9474 110.075 53.3846 108.976 52.7094L108.349 54.7566ZM134.548 49.3169C134.575 49.065 134.634 48.6684 134.634 48.1593C134.634 45.6511 133.45 41.728 128.991 41.728C125.015 41.728 122.593 44.9704 122.593 49.0864C122.593 53.2078 125.101 55.9679 129.307 55.9679C131.478 55.9679 132.973 55.5177 133.846 55.1211L133.423 53.3418C132.49 53.7384 131.419 54.0438 129.645 54.0438C127.164 54.0438 125.02 52.6611 124.961 49.3062H134.548V49.3169ZM124.994 47.5376C125.186 45.8172 126.285 43.502 128.804 43.502C131.596 43.502 132.271 45.9566 132.239 47.5376H124.994ZM137.994 55.6838H140.448V48.4112C140.448 47.9878 140.507 47.5912 140.561 47.2535C140.898 45.3938 142.142 44.0647 143.889 44.0647C144.226 44.0647 144.478 44.0915 144.735 44.1505V41.8138C144.51 41.7548 144.312 41.728 144.033 41.728C142.367 41.728 140.877 42.8803 140.255 44.7186H140.137L140.052 42.0389H137.881C137.967 43.309 137.994 44.6918 137.994 46.2942V55.6838ZM146.509 42.0389L151.696 55.6892H154.07L159.424 42.0389H156.83L154.177 49.7135C153.722 50.9515 153.331 52.0823 153.02 53.2078H152.934C152.65 52.0823 152.286 50.9515 151.83 49.7135L149.151 42.0389H146.509ZM164.531 55.6838V42.0389H162.049V55.6892H164.531V55.6838ZM163.293 36.6527C162.387 36.6527 161.739 37.328 161.739 38.2016C161.739 39.0483 162.36 39.7236 163.239 39.7236C164.225 39.7236 164.847 39.0483 164.815 38.2016C164.815 37.3226 164.225 36.6527 163.293 36.6527ZM178.244 53.3203C177.536 53.6312 176.604 54.001 175.2 54.001C172.488 54.001 170.463 52.0555 170.463 48.8935C170.463 46.0423 172.156 43.7324 175.286 43.7324C176.636 43.7324 177.568 44.0379 178.163 44.3809L178.731 42.4623C178.051 42.1193 176.754 41.7548 175.291 41.7548C170.833 41.7548 167.96 44.7989 167.96 49.006C167.96 53.181 170.64 55.9732 174.755 55.9732C176.588 55.9732 178.024 55.523 178.672 55.18L178.244 53.3203ZM192.653 49.3169C192.68 49.065 192.739 48.6684 192.739 48.1593C192.739 45.6511 191.555 41.728 187.102 41.728C183.126 41.728 180.698 44.9704 180.698 49.0864C180.698 53.2078 183.211 55.9679 187.413 55.9679C189.583 55.9679 191.083 55.5177 191.957 55.1211L191.533 53.3418C190.601 53.7384 189.535 54.0438 187.756 54.0438C185.269 54.0438 183.131 52.6611 183.072 49.3062H192.653V49.3169ZM183.093 47.5376C183.286 45.8172 184.385 43.502 186.898 43.502C189.69 43.502 190.371 45.9566 190.338 47.5376H183.093ZM195.135 55.0407C196.094 55.6034 197.471 55.9679 198.966 55.9679C202.214 55.9679 204.073 54.2797 204.073 51.8787C204.073 49.8475 202.862 48.6684 200.488 47.7627C198.709 47.082 197.894 46.5783 197.894 45.4474C197.894 44.4345 198.709 43.5877 200.177 43.5877C201.447 43.5877 202.433 44.0379 202.969 44.3809L203.591 42.5748C202.83 42.1193 201.619 41.728 200.231 41.728C197.3 41.728 195.526 43.5288 195.526 45.7315C195.526 47.3661 196.683 48.722 199.132 49.5956C200.965 50.2762 201.672 50.9247 201.672 52.1091C201.672 53.2346 200.826 54.1403 199.02 54.1403C197.782 54.1403 196.48 53.6312 195.751 53.1488L195.135 55.0407Z"
				fill={`url(#paint0_linear_159_203_${id})`}
			/>
			<path
				d="M224.886 0.0107422H272.955C280.714 0.0107422 287 6.29728 287 14.0576V55.4587H224.886V0.0107422Z"
				fill={`url(#paint1_linear_159_203_${id})`}
			/>
			<path
				d="M236.284 19.7279C237.008 19.8244 238.074 19.9209 239.58 19.9209C242.093 19.9209 244.151 19.3849 245.502 18.2595C246.739 17.2144 247.634 15.5155 247.634 13.0609C247.634 10.7939 246.798 9.21285 245.464 8.22136C244.226 7.28883 242.645 6.84937 240.207 6.84937C238.739 6.84937 237.351 6.94583 236.284 7.11733V19.7279ZM239.2 9.19141C239.451 9.13781 239.864 9.07886 240.513 9.07886C243.01 9.07886 244.569 10.4884 244.553 13.152C244.553 16.2175 242.838 17.6699 240.207 17.6485C239.843 17.6485 239.446 17.6485 239.194 17.5895V9.19141H239.2Z"
				fill="white"
			/>
			<path
				d="M236.284 43.8074C237.206 43.9307 238.669 44.0593 240.577 44.0593C244.076 44.0593 246.488 43.4215 247.983 42.0549C249.065 41.0044 249.799 39.6056 249.799 37.762C249.799 34.5839 247.415 32.8957 245.378 32.3919V32.3276C247.634 31.4969 249.001 29.6908 249.001 27.6221C249.001 25.9393 248.331 24.6691 247.222 23.8384C245.887 22.7558 244.103 22.2842 241.343 22.2842C239.403 22.2842 237.496 22.4771 236.29 22.729V43.8074H236.284ZM239.055 24.5994C239.5 24.5083 240.228 24.4118 241.504 24.4118C244.301 24.4118 246.204 25.3926 246.204 27.9062C246.204 29.9749 244.489 31.4969 241.563 31.4969H239.055V24.5994ZM239.055 33.5978H241.343C244.36 33.5978 246.873 34.809 246.873 37.7352C246.873 40.849 244.237 41.8995 241.37 41.8995C240.384 41.8995 239.591 41.8727 239.05 41.7762V33.5978H239.055Z"
				fill="white"
			/>
			<path
				d="M255.517 43.8502H257.923V26.2876H255.801L251.787 28.3456L252.27 30.1571L255.463 28.5064H255.517V43.8502Z"
				fill="white"
			/>
			<path
				d="M273.148 28.4797L273.024 30.8003H272.96C272.29 29.5301 270.827 28.126 268.159 28.126C264.633 28.126 261.23 31.0843 261.23 36.299C261.23 40.5597 263.963 43.8021 267.773 43.8021C270.157 43.8021 271.813 42.6552 272.671 41.2243H272.735V42.9393C272.735 46.8194 270.64 48.3147 267.773 48.3147C265.865 48.3147 264.279 47.7412 263.255 47.1035L262.559 49.2365C263.797 50.0672 265.833 50.512 267.676 50.512C269.616 50.512 271.781 50.0672 273.271 48.6684C274.766 47.3339 275.463 45.1688 275.463 41.6369V32.7082C275.463 30.8325 275.527 29.5891 275.591 28.4743H273.148V28.4797ZM272.703 37.3173C272.703 37.7943 272.639 38.3356 272.483 38.8125C271.878 40.5919 270.318 41.6744 268.603 41.6744C265.581 41.6744 264.054 39.1609 264.054 36.1436C264.054 32.5796 265.962 30.3233 268.636 30.3233C270.672 30.3233 272.033 31.6524 272.542 33.2763C272.671 33.6568 272.703 34.0695 272.703 34.5519V37.3173Z"
				fill="white"
			/>
			<path
				d="M257.168 6.84399C252.522 6.84399 248.84 10.14 248.84 15.7566C248.84 21.0624 252.345 24.3906 256.889 24.3906C260.946 24.3906 265.243 21.6841 265.243 15.4779C265.249 10.349 261.991 6.84399 257.168 6.84399ZM257.098 9.13244C260.704 9.13244 262.125 12.7393 262.125 15.5851C262.125 19.3688 259.938 22.1075 257.028 22.1075C254.049 22.1075 251.932 19.3313 251.932 15.6548C251.932 12.466 253.492 9.13244 257.098 9.13244Z"
				fill="white"
			/>
			<path
				d="M214.93 0.0107422H213.386V55.6195H214.93V0.0107422Z"
				fill={`url(#paint2_linear_159_203_${id})`}
			/>
			<path
				d="M137.967 29.2674H143.283V4.58756H151.669V0.133928H129.672V4.58756H137.967V29.2674ZM169.718 20.2315C169.804 19.7546 169.895 18.9775 169.895 17.986C169.895 13.3608 167.687 7.73887 160.817 7.73887C154.076 7.73887 150.528 13.2269 150.528 19.0686C150.528 25.5105 154.547 29.7498 161.374 29.7498C164.402 29.7498 166.91 29.1442 168.721 28.4099L167.944 24.7334C166.347 25.339 164.573 25.682 162.108 25.682C158.738 25.682 155.753 24.0367 155.624 20.2369H169.718V20.2315ZM155.63 16.5175C155.844 14.3523 157.226 11.3725 160.517 11.3725C164.064 11.3725 164.922 14.5667 164.884 16.5175H155.63ZM174.503 29.2674H179.862V0.0856934H174.503V29.2674ZM203.789 20.2315C203.875 19.7546 203.966 18.9775 203.966 17.986C203.966 13.3608 201.758 7.73887 194.888 7.73887C188.147 7.73887 184.599 13.2269 184.599 19.0686C184.599 25.5105 188.618 29.7498 195.445 29.7498C198.473 29.7498 200.981 29.1442 202.792 28.4099L202.015 24.7334C200.418 25.339 198.644 25.682 196.179 25.682C192.809 25.682 189.824 24.0367 189.695 20.2369H203.789V20.2315ZM189.701 16.5175C189.915 14.3523 191.298 11.3725 194.588 11.3725C198.13 11.3725 198.993 14.5667 198.955 16.5175H189.701Z"
				fill={`url(#paint3_linear_159_203_${id})`}
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M0 0H29.7734V8.70897H21.1511V29.2889H8.74016V8.70897H0V0Z"
				fill="#0080C6"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M44.7886 11.9085L38.5349 29.2889H26.6545L38.2831 0H51.1709L63.1853 29.2889H51.1709L44.7886 11.9085Z"
				fill="#0080C6"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M60.3022 0H90.097V8.70897H81.582V29.2889H69.1925V8.70897H60.3022V0Z"
				fill="#0080C6"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M105.091 11.9085L98.8372 29.2889H86.9568L98.5961 0H111.495L123.488 29.2889H111.495L105.091 11.9085Z"
				fill="#0080C6"
			/>
			<defs>
				<linearGradient
					id={`paint0_linear_159_203_${id}`}
					x1="0.357952"
					y1="46.1832"
					x2="204.074"
					y2="46.1832"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#EF7F22" />
					<stop offset="0.2842" stopColor="#E76424" />
					<stop offset="0.5625" stopColor="#E14C26" />
					<stop offset="0.8097" stopColor="#DE3B26" />
					<stop offset="1" stopColor="#DD3526" />
				</linearGradient>
				<linearGradient
					id={`paint1_linear_159_203_${id}`}
					x1="255.943"
					y1="0.0125108"
					x2="255.943"
					y2="55.4615"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#EF7F22" />
					<stop offset="0.2467" stopColor="#E86824" />
					<stop offset="0.551" stopColor="#E24E26" />
					<stop offset="0.8128" stopColor="#DE3C26" />
					<stop offset="1" stopColor="#DD3526" />
				</linearGradient>
				<linearGradient
					id={`paint2_linear_159_203_${id}`}
					x1="214.159"
					y1="0.0125108"
					x2="214.159"
					y2="55.6196"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#EF7F22" />
					<stop offset="1" stopColor="#DD3526" />
				</linearGradient>
				<linearGradient
					id={`paint3_linear_159_203_${id}`}
					x1="129.669"
					y1="14.9171"
					x2="203.966"
					y2="14.9171"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#EF7F22" />
					<stop offset="0.3614" stopColor="#E76324" />
					<stop offset="0.7764" stopColor="#DF4326" />
					<stop offset="1" stopColor="#DD3526" />
				</linearGradient>
			</defs>
		</svg>
	)
}
